export const PRACTICE_EDITABLE_DEFAULTS = {
  practice_name: '' as string,
  practice_address: null as string | null,
  practice_language: null as string | null,
  practice_logo: null as string | null,
  primary_network_id: null as string | null,
  is_archived: false as boolean,
};

export type PracticeEditableValues = typeof PRACTICE_EDITABLE_DEFAULTS;
export type EditablePracticeField = keyof PracticeEditableValues;
export const EDITABLE_PRACTICE_FIELDS = Object.keys(
  PRACTICE_EDITABLE_DEFAULTS
) as EditablePracticeField[];

type NullableEditablePracticeField = Exclude<
  EditablePracticeField,
  'practice_name' | 'is_archived'
>;

export type PracticeEditableSource = Pick<
  PracticeEditableValues,
  'practice_name' | 'is_archived'
> &
  Partial<Pick<PracticeEditableValues, NullableEditablePracticeField>>;

export type PracticeRowDraft = PracticeEditableValues;
export type PracticeProposedChanges = Partial<PracticeEditableValues>;

export interface PracticeChangeRequestLike {
  proposed_changes?: unknown;
}

export interface PracticeWithChangeRequests extends PracticeEditableSource {
  change_requests: PracticeChangeRequestLike[];
}

const hasOwn = (value: object, field: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(value, field);

const pickFields = <T extends object>(
  source: T,
  fields: readonly (keyof T)[]
) =>
  Object.fromEntries(fields.map((field) => [field, source[field]])) as Pick<
    T,
    typeof fields[number]
  >;

const pickPresentFields = <T extends object>(
  source: Partial<T>,
  fields: readonly (keyof T)[]
) =>
  Object.fromEntries(
    fields
      .filter((field) => hasOwn(source, field))
      .map((field) => [field, source[field]])
  ) as Partial<T>;

export const getLivePracticeValues = (
  practice: PracticeEditableSource
): PracticeEditableValues =>
  (Object.fromEntries(
    EDITABLE_PRACTICE_FIELDS.map((field) => [
      field,
      practice[field] ?? PRACTICE_EDITABLE_DEFAULTS[field],
    ])
  ) as unknown) as PracticeEditableValues;

export const requestToPracticeProposedChanges = (
  request?: PracticeChangeRequestLike
): PracticeProposedChanges =>
  pickPresentFields(
    (request?.proposed_changes || {}) as PracticeProposedChanges,
    EDITABLE_PRACTICE_FIELDS
  );

export const getPracticeBaseline = (
  practice: PracticeWithChangeRequests
): PracticeRowDraft => {
  const live = getLivePracticeValues(practice);
  const proposed = requestToPracticeProposedChanges(
    practice.change_requests[0]
  );

  return {
    ...live,
    ...proposed,
    // The database column is required; ignore malformed JSON nulls here so
    // the inline name editor always receives a string.
    practice_name: proposed.practice_name ?? live.practice_name,
  };
};

export const draftToPracticeProposedChanges = (
  practice: PracticeEditableSource,
  draft: PracticeRowDraft
): PracticeProposedChanges => {
  const live = getLivePracticeValues(practice);
  const changedFields = EDITABLE_PRACTICE_FIELDS.filter(
    (field) => draft[field] !== live[field]
  );
  return pickFields(draft, changedFields);
};

export const originalValuesForPracticeChanges = (
  practice: PracticeEditableSource,
  proposed: PracticeProposedChanges
): PracticeProposedChanges => {
  const live = getLivePracticeValues(practice);
  return pickFields(
    live,
    EDITABLE_PRACTICE_FIELDS.filter((field) => hasOwn(proposed, field))
  );
};

export const practiceDraftsEqual = (a: PracticeRowDraft, b: PracticeRowDraft) =>
  EDITABLE_PRACTICE_FIELDS.every((field) => a[field] === b[field]);

export const practiceProposedChangesEqual = (
  a: PracticeProposedChanges,
  b: PracticeProposedChanges
) =>
  EDITABLE_PRACTICE_FIELDS.every(
    (field) => hasOwn(a, field) === hasOwn(b, field) && a[field] === b[field]
  );
